import {useEffect, useState} from "react";
import {Button, Modal} from "antd";
import {CheckCircleFilled} from "@ant-design/icons";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {browserSupportsWebAuthn} from "@simplewebauthn/browser";
import {globalNotificationState, globalUserVerifyState} from "./global_state";
import METHOD_ICONS from "./twoFactorMethodIcons";
import usePasskeyEnrolment from "./usePasskeyEnrolment";
import {
    cannotEnrolHere,
    consumePasskeyPrompt,
    markCannotEnrolHere,
    passkeyWorkedHere,
} from "./passkeyPromptState";
import {getUserPasskeysQuery} from "../api/graphql/userPasskey";
import {PASSKEY_PROMPT_Z_INDEX} from "./passkeyPromptZIndex";

// undefined where the browser cannot answer, which is not the same as a no
const platformAuthenticatorAvailable = async () => {
    try {
        return await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
        return false;
    }
};

// the label carries its own punctuation, which is not a colon in every language
const BENEFITS = [
    {
        label: ["passkeyPrompt.securityLabel", "Better security:"],
        text: ["passkeyPrompt.securityText", "your account is protected by your device"],
    },
    {
        label: ["passkeyPrompt.signInLabel", "Easier sign in:"],
        text: ["passkeyPrompt.signInText", "Face ID, fingerprint or PIN instead of a password"],
    },
];

// /login still wears the login palette, and verification is a flow to finish first
const SKIP_PATHS = [
    "/login",
    "/register",
    "/forgot-password",
    "/verify-email",
    "/auth/callback",
    "/account-verification",
];

// offered once per login, to a client with no passkey on the account
const PasskeyPromptModal = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const verifyState = useRecoilValue(globalUserVerifyState);

    const [open, setOpen] = useState(false);
    const [platformAuthenticator, setPlatformAuthenticator] = useState(null);
    // no webauthn here at all, so the dialog offers the way out instead of the ceremony
    const [unsupported, setUnsupported] = useState(false);
    // the account holds a passkey, just none that has worked on this device
    const [elsewhere, setElsewhere] = useState(false);

    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    // an in-app browser can never enrol, so the way forward is to reopen the page elsewhere
    const copyLink = async () => {
        const url = window.location.href;

        try {
            await navigator.clipboard.writeText(url);
        } catch (error) {
            const field = document.createElement("textarea");

            field.value = url;
            document.body.appendChild(field);
            field.select();
            document.execCommand("copy");
            field.remove();
        }

        setNotificationMessage({
            status: "success",
            title: "",
            msg: t("twoFactor.linkCopied", {defaultValue: "Link copied"}),
            key: "passkeyPromptLinkCopied",
        });
        setOpen(false);
    };

    const {enrol, busy} = usePasskeyEnrolment({
        layoutKey: "PasskeyPrompt",
        onEnrolled: () => setOpen(false),
        // the hook has already said where to go next, so the prompt gets out of the way
        onBlocked: () => setOpen(false),
    });

    useEffect(() => {
        // logging out ends the ask, whether it is already on screen or still being decided
        if (!verifyState) {
            setOpen(false);
            return;
        }

        if (SKIP_PATHS.some((path) => location.pathname.startsWith(path))) return;
        // one shot: a second login in the same tab marks it again, a refresh does not
        if (!consumePasskeyPrompt()) return;

        let live = true;

        (async () => {
            try {
                // the cache outlives a logout, so a shared browser would answer for
                // whoever logged in before
                const response = await getUserPasskeysQuery("network-only");
                const payload = response?.getUserPasskeys;

                if (payload?.__typename !== "UserPasskeyArray") return;

                // not the 2fa policy: a passkey is a login method in its own right
                if (!payload?.rpId) return;

                // not data.length alone: one bound to their laptop is no use on their phone
                if (passkeyWorkedHere() && payload?.data?.length) return;

                // an in-app browser can only be escaped, so it gets the cannot-enrol retry
                if (!browserSupportsWebAuthn()) {
                    if (!cannotEnrolHere()) {
                        markCannotEnrolHere();
                        setUnsupported(true);
                        setOpen(true);
                    }

                    return;
                }

                if (cannotEnrolHere()) return;

                // resolved before opening, or the wording would change under the client
                setPlatformAuthenticator(await platformAuthenticatorAvailable());

                // a prompt raised after logout would land on the login screen
                if (!live) return;

                setElsewhere(!!payload?.data?.length);
                setOpen(true);
            } catch (error) {
                // a list we could not read is not a reason to ask for a passkey
            }
        })();

        return () => {
            live = false;
        };
    }, [verifyState, location.pathname]);

    return (
        <Modal
            open={open}
            title={
                elsewhere
                    ? t("passkeyPrompt.titleThisDevice", {defaultValue: "Add a passkey to this device"})
                    : t("passkeyPrompt.title", {defaultValue: "Protect your account with a passkey"})
            }
            onCancel={() => setOpen(false)}
            footer={null}
            destroyOnClose
            width={400}
            // This dialog portals to document.body, so the layout's own
            // `#layout-3 .ant-modal-wrap { z-index: 1200 }` never matches it and it falls back to
            // antd's default 1000 -- UNDER the header (1002) and the sider (1001). The chrome then
            // sits on top of the dialog and swallows the clicks meant for Later and the close
            // button, so the prompt could not be dismissed by any means and the mask left the
            // portal unnavigable. TwoFactorChallengeModal, mounted beside this one in App.js and
            // portalling the same way, already carries zIndex 1100 for exactly this reason.
            zIndex={PASSKEY_PROMPT_Z_INDEX}
            // Stated rather than implied: both are antd defaults today, and both are the only ways
            // out of a dialog whose footer is null.
            maskClosable
            keyboard
        >
            <div style={{display: "flex", flexDirection: "column", gap: 14, alignItems: "center"}}>
                <div
                    style={{
                        width: 96,
                        padding: "16px 8px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                        border: "1px solid var(--divider-color)",
                        borderRadius: 10,
                        background: "var(--primary-card-bg-color)",
                        color: "var(--primary-text-color)",
                    }}
                >
                    <span style={{height: 24, display: "flex", alignItems: "center"}}>
                        {METHOD_ICONS.passkey}
                    </span>
                    <span style={{fontSize: 12}}>
                        {t("twoFactor.methodPasskey", {defaultValue: "Passkey"})}
                    </span>
                </div>

                {/* the reason to act now, rather than a convenience they can put off */}
                <div
                    style={{
                        fontSize: 13,
                        color: "var(--primary-text-color)",
                        lineHeight: 1.5,
                        textAlign: "center",
                    }}
                >
                    {unsupported
                        ? t("twoFactor.passkeyBrowserHint", {
                              defaultValue:
                                  "This browser doesn't support passkeys. Open this page in Chrome to add one.",
                          })
                        : elsewhere
                          ? t("passkeyPrompt.notOnThisDevice", {
                                defaultValue: "This device doesn't have a passkey set up yet.",
                            })
                          : t("passkeyPrompt.required", {
                                defaultValue:
                                    "Some actions on your account can only be confirmed with a passkey.",
                            })}
                </div>

                {/* --note-text-color is near invisible on the darker brands */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        width: "100%",
                        textAlign: "left",
                        color: "var(--primary-text-color)",
                    }}
                >
                    {BENEFITS.map(({label, text}) => (
                        <div
                            key={label[0]}
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 8,
                                fontSize: 13,
                                lineHeight: 1.4,
                            }}
                        >
                            {/* one line box tall, so the tick centres on the first line at
                                any font size, and wrapping text cannot squash it */}
                            <span
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    height: "1.4em",
                                    flexShrink: 0,
                                }}
                            >
                                <CheckCircleFilled style={{color: "var(--primary-btn-bg-color)"}} />
                            </span>
                            <span>
                                <span style={{fontWeight: 600}}>
                                    {t(label[0], {defaultValue: label[1]})}
                                </span>{" "}
                                {t(text[0], {defaultValue: text[1]})}
                            </span>
                        </div>
                    ))}
                </div>

                {/* explains the qr code that appears in place of a fingerprint prompt */}
                {!unsupported && (
                    <div
                        style={{
                            fontSize: 12,
                            color: "var(--primary-text-color)",
                            opacity: 0.7,
                            lineHeight: 1.5,
                            textAlign: "center",
                        }}
                    >
                        {platformAuthenticator === false
                            ? t("passkeyPrompt.noScreenLock", {
                                  defaultValue:
                                      "No unlock on this device, so your browser will offer your phone.",
                              })
                            : t("passkeyPrompt.verifyNote", {
                                  defaultValue: "We will email a code to confirm it is you.",
                              })}
                    </div>
                )}

                <Button
                    type={"primary"}
                    block
                    loading={busy}
                    onClick={unsupported ? copyLink : enrol}
                    style={{
                        // the dialog is a portal, out of reach of the brand stylesheet
                        backgroundColor: "var(--primary-btn-bg-color)",
                        color: "var(--primary-btn-text-color)",
                        borderColor: "var(--primary-btn-border-color)",
                    }}
                >
                    {unsupported
                        ? t("twoFactor.copyLink", {defaultValue: "Copy link"})
                        : t("twoFactor.setUpPasskey", {defaultValue: "Set up passkey"})}
                </Button>

                {/* no snooze behind it: a session lasts 12 hours, so the next login asks again */}
                <div
                    role={"button"}
                    tabIndex={0}
                    onClick={() => setOpen(false)}
                    onKeyDown={(event) =>
                        (event.key === "Enter" || event.key === " ") && setOpen(false)
                    }
                    style={{
                        fontSize: 13,
                        cursor: "pointer",
                        color: "var(--link-color, var(--primary-btn-bg-color))",
                    }}
                >
                    {t("common.later", {defaultValue: "Later"})}
                </div>
            </div>
        </Modal>
    );
};

export default PasskeyPromptModal;
